import { getCountryCodeByTopLevelDomain } from '@/helpers';
import {
  CmsLink,
  PromoBanner,
  RewardsBanner,
  LargeOrderPromo,
  LargeOrderPromoCard,
  JoinRewards,
  CmsLinkLocation,
  CmsLinkTarget,
  LegalCopy,
  GuacMode,
  SystemFlags,
  FeaturedPCE,
  OrderWebMetadata,
  OrderWebTitleDescription,
  LegalCopyCreateAccount,
  CreateAccountSignInContentFragment,
  OrderConfirmationCreateAccountExperience,
} from '@/models/cms';
import { CountryCode } from '@/store/ui';

export function getPromoBanner(cmsBaseUrl: string, banner?: AemPromoBanner): PromoBanner | null {
  const content = banner && banner.master;
  if (!content) {
    return null;
  }
  const promo = {
    showPromoBanner: content.showPromoBanner || false,
    showAdaptiveHero: content.showAdaptiveHero ? content.showAdaptiveHero : false,
    nooHomePageURL: content.nooHomePageURL,
    showNewHero: content.showNewHero ? content.showNewHero : false,
    showHero: content.showHero ? content.showHero : false,
    dualPromo: content.dualPromo,
    centerAlignedTextOnly: content.centerAlignedTextOnly,
    isContentRightAligned: content.isContentRightAligned,
    shouldPlaceMobileTitleBelowImage: content.titleMainPlacement,
    primary: {
      backgroundImageUrl: content.backgroundImageUrl ? `${cmsBaseUrl}${content.backgroundImageUrl}` : undefined,
      backgroundVideoUrl: content.backgroundVideoUrl ? `${cmsBaseUrl}${content.backgroundVideoUrl}` : undefined,
      shouldLoopVideo: content.shouldLoopVideo,
      title: content.titleMain,
      preTitle: content.preTitleMain,
      preTitleColor: content.preTitleMainColor,
      titleColor: content.titleMainColor,
      subtitle: content.subtitleMain,
      subtitleColor: content.subtitleMainColor,
      legalText: content.legalTextMain,
      legalTextColor: content.legalTextColorMain,
      buttonText: content.buttonTextMain,
      buttonLink: content.buttonLinkMain,
      buttonColor: content.buttonColorMain,
      imageUrlDesktop: content.imageUrlMainDesktop ? `${cmsBaseUrl}${content.imageUrlMainDesktop}` : undefined,
      imageUrlMobile: content.imageUrlMainMobile ? `${cmsBaseUrl}${content.imageUrlMainMobile}` : undefined,
      videoUrlDesktop: content.videoUrlMainDesktop ? `${cmsBaseUrl}${content.videoUrlMainDesktop}` : undefined,
      videoUrlMobile: content.videoUrlMainMobile ? `${cmsBaseUrl}${content.videoUrlMainMobile}` : undefined,
      shouldLoopBottomVideo: content.shouldLoopMainVideo,
      linkCategory: content.linkCategoryMain
    },
  };
  // SecondaryButton split out to support two CTAs in primary hero mode
  const secondaryButton = {
    buttonText: content.buttonTextSecondary,
    buttonLink: content.buttonLinkSecondary,
    buttonColor: content.buttonColorSecondary,
  };
  const supportsSecondaryButton = secondaryButton.buttonText && secondaryButton.buttonLink;
  const secondary = {
    title: content.titleSecondary,
    titleColor: content.titleSecondaryColor,
    subtitle: content.subtitleSecondary,
    subtitleColor: content.subtitleSecondaryColor,
    legalText: content.legalTextSecondary,
    legalTextColor: content.legalTextColorSecondary,
    imageUrlMobile: content.imageUrlSecondaryMobile ? `${cmsBaseUrl}${content.imageUrlSecondaryMobile}` : undefined,
    imageUrlDesktop: content.imageUrlSecondaryDesktop ? `${cmsBaseUrl}${content.imageUrlSecondaryDesktop}` : undefined,
    videoUrlMobile: content.videoUrlSecondaryMobile ? `${cmsBaseUrl}${content.videoUrlSecondaryMobile}` : undefined,
    videoUrlDesktop: content.videoUrlSecondaryDesktop ? `${cmsBaseUrl}${content.videoUrlSecondaryDesktop}` : undefined,
    shouldLoopBottomVideo: content.shouldLoopSecondaryVideo,
    linkCategory: content.linkCategorySecondary,
    ...secondaryButton,
  };

  return promo.dualPromo ? { ...promo, secondary } : supportsSecondaryButton ? { ...promo, secondary: secondaryButton } : promo;
}

// TODO-7v3td2: remove this stub and add state directly to component
export function getHeaderLinks(cateringUrl: string): CmsLink[] {
  return [
    { type: 'text', text: 'Menu', url: '/#menu', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Catering', url: cateringUrl, location: CmsLinkLocation.External, target: CmsLinkTarget.Blank },
    { type: 'text', text: 'Rewards', url: '/order/rewards', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Offers', url: '/order/offers', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Our Values', url: '/values', location: CmsLinkLocation.External, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Nutrition', url: '/nutrition-calculator', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
  ];
}

export function getFeaturedHeader(featuredHeaderData: AemFeaturedHeader): string | null {
  const elements = featuredHeaderData && featuredHeaderData.master;
  if (!elements) {
    return null;
  }

  return elements.featuredTitle || null;
}

export function getGuacMode(guacmodeData?: AemGuacMode): GuacMode | null {
  return (guacmodeData && guacmodeData.master) || null;
}

export function getFeaturedPCE(featuredPCEdata?: AemFeaturedPCE): FeaturedPCE {
  const PCEData = featuredPCEdata && featuredPCEdata.master;
  if (!PCEData) {
    return {
      header: 'FEATURED',
      subHeader: '',
      restaurantSelectedHeader: 'FEATURED',
    };
  }

  return {
    header: PCEData.header,
    subHeader: PCEData.subHeader,
    restaurantSelectedHeader: PCEData.restaurantSelectedHeader
  };
}

export function getRewardsBanner(cmsBaseUrl: string, rewardsBannerData?: AemRewardsBanner): RewardsBanner {
  const rewardsData = rewardsBannerData && rewardsBannerData.master;
  if (!rewardsData) {
    return {
      enableRewardsBanner: false,
      medallionImage: '',
      description: '',
      ctaSelecter: 'Standard',
      createAccountButtonLabel: '',
      dynamicCtaUrl: '',
      dynamicCtaButtonLabel: '',
      signInButtonLabel: '',
    };
  }

  return {
    enableRewardsBanner: rewardsData.enableRewardsBanner,
    medallionImage: `${cmsBaseUrl}${rewardsData.medallionImage}`,
    description: rewardsData.description,
    ctaSelecter: rewardsData.ctaSelecter,
    createAccountButtonLabel: rewardsData.createAccountButtonLabel,
    dynamicCtaUrl: rewardsData.dynamicCtaUrl,
    dynamicCtaButtonLabel: rewardsData.dynamicCtaButtonLabel,
    signInButtonLabel: rewardsData.signInButtonLabel,
  };
}

export function getLargeOrderPromo(cmsBaseUrl: string, largeOrderPromoData?: AemLargeOrderPromo): LargeOrderPromo | null {
  const content = largeOrderPromoData && largeOrderPromoData.master;
  if (!content) {
    return null;
  }

  return {
    mobileBackgroundImageUrl: `${cmsBaseUrl}${content.mobileBackgroundImageUrl}`,
    desktopBackgroundImageUrl: `${cmsBaseUrl}${content.desktopBackgroundImageUrl}`,
    mobileBackgroundColor: content.mobileBackgroundColor,
    desktopBackgroundColor: content.desktopBackgroundColor,
    preTitle: content.preTitle,
    preTitleColor: content.preTitleColor,
    title: content.title,
    titleColor: content.titleColor,
  };
}

export function getLargeOrderCard(cmsBaseUrl: string, groupOrderCardData?: AemLargeOrderCard): LargeOrderPromoCard | null {
  const content = groupOrderCardData && groupOrderCardData.master;
  if (!content) {
    return null;
  }

  return {
    title: content.title,
    titleColor: content.titleColor,
    backgroundColor: content.backgroundColor,
    imageUrl: `${cmsBaseUrl}${content.imageUrl}`,
    navUrl: content.navigationLink,
    detailsCopy: content.detailsCopy,
    detailsCopyColor: content.detailsCopyColor,
    boldCopy: content.boldCopy,
    boldCopyColor: content.boldCopyColor,
    buttonText: content.buttonText,
    buttonTextColor: content.buttonTextColor,
    buttonColor: content.buttonColor,
    linkCategory: content.linkCategory
  };
}


export function getSystemFlags(systemFlags: AemJSON): SystemFlags {
  try {
    const jsonCleaned = new DOMParser().parseFromString(systemFlags.master.json, 'text/html');
    return JSON.parse(jsonCleaned.body.textContent || '');
  } catch {
    // fallback values
    return {
      headerPages: ['/guacmode'],
    };
  }
}

export function getMetadata(metadata: AemMetadata): OrderWebMetadata | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  return {
    title: content.googleSearchResultTitle,
    description: content.googleSearchResultDescription,
  };
}
export function getOrderWebTitleAndDescription(metadata: AemMetadata): OrderWebTitleDescription | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  return {
    title: content.googleSearchResultTitle,
    description: content.googleSearchResultDescription,
  };
}

export function getNutritionPath(aemNutritionPath: AemNutritionPath): string | undefined {
  const content = aemNutritionPath && aemNutritionPath.master;
  if (!content) {
    return undefined;
  }

  return content.fullNutritionURL;
}

export function getOrderConfirmationGuest(cmsBaseUrl: string, metadata: AEMGuestOrderCreateAccountFragment): OrderConfirmationCreateAccountExperience | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  const orderConfirmationGuest = {
    header: content['header-text'],
    description: content['subtext-description'],
    medallionImageUrl: `${cmsBaseUrl}${content['medallion-image']}`
  };

  return orderConfirmationGuest;
}